import React, { useEffect } from 'react';
import ReactTooltip from 'react-tooltip';
import { makeStyles } from 'design-system/styles';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';

const useStyles = makeStyles<{ isBlue?: boolean }>({
  wrapper: {
    borderRadius: '4px',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s',
    fontSize: '14px',
  },
  noShrink: {
    flexShrink: 0,
  },
  off: {
    color: 'rgba(0, 0, 0, 0.5)',
    '&:hover': {
      color: 'rgba(0, 0, 0, 0.6)',
    },
    '&:active': {
      color: 'rgba(0, 0, 0, 0.8)',
    },
  },
  on: {
    background: props => (props.isBlue ? 'rgba(160, 211, 240, 0.7)' : 'rgba(0, 0, 0, 0.1)'),
    color: props => (props.isBlue ? 'rgba(43, 153, 216, 1.0)' : 'rgba(0, 0, 0, 0.5)'),
    '&:hover': {
      color: props => (props.isBlue ? 'rgba(43, 153, 216, 1.0)' : 'rgba(0, 0, 0, 0.6)'),
    },
    '&:active': {
      background: props => (props.isBlue ? 'rgba(160, 211, 240, 1.0)' : 'rgba(0, 0, 0, 0.2)'),
      color: props => (props.isBlue ? 'rgba(43, 153, 216, 1.0)' : 'rgba(0, 0, 0, 0.8)'),
    },
  },
  disabled: {
    pointerEvents: 'none',
    opacity: 0.2,
  },
});

const IconToggle = (props: {
  name?: string;
  icon: $FIXME;
  tooltip?: React.ReactNode;
  isDisabled?: boolean;
  isOn: boolean;
  invertIcon?: boolean;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onRightClick?: React.MouseEventHandler<HTMLDivElement>;
  onHover?: React.MouseEventHandler<HTMLDivElement>;
  onBlur?: React.MouseEventHandler<HTMLDivElement>;
  color?: string;
  placeTop?: boolean;
  /**
   * Boolean for whether the on color is blue (true) or gray (false/undefined).
   */
  isBlue?: boolean;
  noShrink?: boolean;
  style?: React.CSSProperties;
  size?: SizeProp;
  iconStyle?: FontAwesomeIconProps['style'];
}) => {
  const {
    name,
    icon,
    tooltip,
    isDisabled,
    isOn,
    onClick,
    onRightClick,
    onHover,
    onBlur,
    invertIcon,
    color,
    placeTop,
    noShrink,
    style,
    size,
    iconStyle,
  } = props;

  const classes = useStyles(props);
  const hasTooltip = !!(name && tooltip);

  // Add document click handler to hide all tooltips
  useEffect(() => {
    const handleDocumentClick = () => {
      ReactTooltip.hide();
    };

    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, []);

  return (
    <div
      className={classnames(classes.wrapper, {
        [classes.off]: !isOn,
        [classes.on]: isOn,
        [classes.disabled]: isDisabled,
        [classes.noShrink]: noShrink,
      })}
      style={{ ...style, color: color ?? '' }}
      onClick={onClick}
      onContextMenu={onRightClick}
      onMouseEnter={onHover}
      onMouseLeave={onBlur}
      data-for={hasTooltip ? `toggleable-icon-${name}` : null}
      data-tip={hasTooltip ? true : null}
      data-effect="solid"
    >
      <FontAwesomeIcon icon={icon} inverse={invertIcon} size={size} style={iconStyle} />

      {hasTooltip && (
        <ReactTooltip place={placeTop ? 'top' : 'right'} id={`toggleable-icon-${name}`}>
          {tooltip}
        </ReactTooltip>
      )}
    </div>
  );
};

export default React.memo(IconToggle);
